import * as React from "react";
import { Card, Form } from "react-bootstrap";
import { toast } from "react-toastify";
import { gettext } from "i18n";
import {
  OrganizationMfaSettings as Settings,
  loadOrganizationMfa,
  updateOrganizationMfa,
} from "utils/mfa";
import { StepUpCancelled, useStepUp } from "./StepUpProvider";

/**
 * Whether an organization's members must use MFA, and whether they may use
 * email codes: set by the organization's admins. A global admin may also
 * enforce MFA, which the organization's admins cannot turn off.
 */
const OrganizationMfaSettings: React.FC<{ organizationId: number }> = ({
  organizationId,
}) => {
  const [settings, setSettings] = React.useState<Settings | null>(null);
  const [saving, setSaving] = React.useState<boolean>(false);
  const withStepUp = useStepUp();

  React.useEffect(() => {
    loadOrganizationMfa(organizationId)
      .then(setSettings)
      // e.g. not an admin of the organization: nothing to show
      .catch(() => setSettings(null));
  }, [organizationId]);

  const change = async (field: keyof Settings, value: boolean) => {
    if (
      value &&
      (field === "mfa_required" || field === "mfa_enforced") &&
      !window.confirm(
        gettext(
          "Members without MFA will have to set it up before they can do anything else. Continue?",
        ),
      )
    ) {
      return;
    }
    setSaving(true);
    try {
      // Changing members' MFA needs the admin's own second factor confirmed
      setSettings(
        await withStepUp(() =>
          updateOrganizationMfa(organizationId, { [field]: value }),
        ),
      );
      toast.success(gettext("Organization updated successfully"));
    } catch (e) {
      if (!(e instanceof StepUpCancelled)) {
        toast.error(gettext((e as Error).message));
      }
    } finally {
      setSaving(false);
    }
  };

  if (!settings) {
    return null;
  }

  return (
    <Card className="mt-3">
      <Card.Body>
        <h5>{gettext("Security")}</h5>
        <Form.Check
          type="switch"
          id="organization-mfa-required"
          label={gettext(
            "Require multi-factor authentication from all members",
          )}
          checked={settings.mfa_required || settings.mfa_enforced}
          disabled={saving || settings.mfa_enforced}
          onChange={(e) => change("mfa_required", e.target.checked)}
        />
        {settings.mfa_enforced && (
          <Form.Text muted className="d-block mb-2">
            {gettext(
              "A global admin has made MFA mandatory for this organization.",
            )}
          </Form.Text>
        )}
        <Form.Check
          type="switch"
          id="organization-mfa-app-only"
          label={gettext(
            "Allow only authenticator apps and passkeys (no codes by email)",
          )}
          checked={settings.mfa_app_only}
          disabled={saving}
          onChange={(e) => change("mfa_app_only", e.target.checked)}
        />
        {settings.can_enforce && (
          <Form.Check
            type="switch"
            id="organization-mfa-enforced"
            label={gettext(
              "Enforce multi-factor authentication (organization admins cannot turn it off)",
            )}
            checked={settings.mfa_enforced}
            disabled={saving}
            onChange={(e) => change("mfa_enforced", e.target.checked)}
          />
        )}
      </Card.Body>
    </Card>
  );
};

export default OrganizationMfaSettings;
